import * as React from 'react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import ActionButtons from './ActionButtons';
import { showModal } from '@actions/common/actionCreators';
import { configs } from '@constants/configs/configs';
import { dashboardModalTypes } from '@constants/individualPortal/dashboard/modals/dashboardModalTypes';
import useTobaccoExemptData from './useTobaccoExempt';
import TobaccoAlert from './TobaccoAlert';
import TobaccoExemptHeader from './TobaccoExemptHeader';
import HardshipExemptionForm from './HardshipExemptionForm';
import GITextIntl from '@components/common/app-components/GIText/GITextIntl';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import { IDataToSubmit } from './types/apiResponse';
import Spinner from '@components/common/spinner/Spinner';
import { useModal } from '@hooks/index';
import { modalConfig } from '@modules/custom-grouping/modals/modalConfig';
import withModalWrapper from '@containers/common/WithModalWrapper';

const TobaccoExemptContainer = () => {
    const reduxDispatch = useDispatch();
    const navigate = useNavigate();
    const { openModal } = useModal();
    // Use the hook and extract properties for handling the tobacco exemption data.
    const {
        additionalInfoData,
        caseNumber,
        environment,
        getAdditionalInfo,
        isHardshipExemptCheckBoxVisible,
        isSavingError,
        onSaveAndContinue,
        updateTobaccoUse,
        updateExemptionNumber,
        updateHardExemption,
        updateHasDisability,
    } = useTobaccoExemptData({
        navigate,
        dispatch: reduxDispatch,
    });

    const {
        isMNFullStack,
        stateCode,
        screenerConfigs: { iexCurrentCoverageYear, iexPreviousCoverageYear },
        exchangeName,
        language,
        links: {
            PHONE,
        }
    } = environment;

    const { routes } = configs;
    // Destructure data for additional information and loading states.
    const { additionalInfo, isError } = additionalInfoData;
    // Extract eligibility details and check for existence.
    const eligibilityDetails = additionalInfo?.applicantDetails?.eligiblitydetails || [];
    // Extract and compute boolean flags based on the additionalInfo data.
    const isCoverageYearOver = additionalInfo?.coverageYearOver || false;
    const isHardExemptionChecked = additionalInfo?.householdExempt || false;
    const isEligibilityDetailsExist = eligibilityDetails.length > 0;

    useEffect(() => {
        if (caseNumber) {
            getAdditionalInfo();
        }
    }, [caseNumber]);

    // Open modal if there is an error ( isSavingError is true )
    useEffect(() => {
        if (isSavingError) {
            reduxDispatch(showModal({ modalData: {}, modalType: dashboardModalTypes.FAILURE_MODAL }));
        }
    }, [isSavingError, reduxDispatch]);

    const gotoDashboard = () => {
        navigate(routes.INDPORTAL);
    };

    const dataToSubmit: IDataToSubmit = {
        applicantDetails: {
            eligiblitydetails: eligibilityDetails,
            ...(additionalInfo?.applicantDetails?.inEligibilitydetails && {
                inEligibilitydetails: additionalInfo.applicantDetails.inEligibilitydetails,
            }),
        },
        householdExempt: isHardExemptionChecked,
        isChangePlanFlow: additionalInfo?.isChangePlanFlow || false,
        isSEPPlanFlow: additionalInfo?.isSEPPlanFlow || false,
    };
    const onHandleSaveAndContinue = async () => {
        try {
            await onSaveAndContinue(dataToSubmit, isHardExemptionChecked, eligibilityDetails, openModal);
        } catch (error) {
            console.error('Error saving data:', error);
        }
    };
    if (isError) {
        return <TobaccoAlert messageId="indportal.tobacco.additionalInfo.error" id="additional-info-error" textReplacements={{ exchangeName }} />;
    }
    if (isCoverageYearOver) {
        return (
            <TobaccoAlert
                messageId="indportal.tobacco.coverageYearOver.error"
                id="coverageYearOver-error"
                textReplacements={{
                    currentCoverageYear: iexCurrentCoverageYear,
                    previousCoverageYear: iexPreviousCoverageYear,
                    PHONE,
                    exchangeName,
                    b: chunks => <strong>{chunks}</strong>,
                }}
            />
        );
    }
    if (!additionalInfo) {
        return <Spinner message="Loading..." />;
    }
    return (
        <>
            <div className="grid-row" id="tobacco-exempt">
                <div className="grid-col-12 tablet:grid-col-12">
                    <TobaccoExemptHeader />
                    <div id={'body-wrapper'} className={'body-wrapper'}>
                        <GITextIntl
                            id={'additional-info__need-details'}
                            messageId={'indportal.tobacco.info.needDetails'}
                            textReplacements={{ exchangeName, br: <br /> }}
                        />
                        <ConditionalRendering isTrue={additionalInfo?.showTobaccoCheckBox}>
                            <TobaccoAlert
                                messageId={'indportal.tobacco.alert.tobaccoUseContent'}
                                id={'tobacco-use-content'}
                                textReplacements={{
                                    strong: chunks => <strong>{chunks}</strong>,
                                }}
                            />
                        </ConditionalRendering>
                        <ConditionalRendering isTrue={isHardshipExemptCheckBoxVisible}>
                            <TobaccoAlert
                                messageId={'indportal.tobacco.alert.hardshipExemptContent'}
                                id={'hardship-exempt-content'}
                                textReplacements={{
                                    strong: chunks => <strong id="hardship-exempt-heading">{chunks}</strong>,
                                    a: chunks => (
                                        <a
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-labelledby="hardship-exempt-link hardship-exempt-heading"
                                            href={configs.endpoints.externalUrls.hardshipExemptContentLink[language.toUpperCase()][stateCode]}
                                        >
                                            <span id="hardship-exempt-link" className="sr-only">Learn more about</span>
                                            {chunks}
                                        </a>
                                    ),
                                }}
                            />
                        </ConditionalRendering>
                        <ConditionalRendering isTrue={additionalInfo?.showBlindOrDisabilityCheckBox}>
                            <TobaccoAlert
                                messageId={'indportal.tobacco.alert.hasDisabilityContent'}
                                id={'blind-or-disability-content'}
                                textReplacements={{
                                    strong: chunks => <strong>{chunks}</strong>,
                                }}
                            />
                        </ConditionalRendering>

                        <HardshipExemptionForm
                            additionalInfo={additionalInfo}
                            isHardExemptionChecked={isHardExemptionChecked}
                            isHardshipExemptCheckBoxVisible={isHardshipExemptCheckBoxVisible}
                            onHandleExemptionNumberChange={updateExemptionNumber}
                            isMNFullStack={isMNFullStack}
                            onHandleHardExemptionChange={checked => updateHardExemption(checked)}
                            onHandleTobaccoUseChange={personId => updateTobaccoUse(personId)}
                            onHandleHasDisabilityChange={personId => updateHasDisability(personId)}
                            stateCode={stateCode}
                            eligibilityDetails={eligibilityDetails}
                        />

                        <ActionButtons
                            isCoverageYearOver={isCoverageYearOver}
                            gotoDashboard={gotoDashboard}
                            isEligibilityDetailsExist={isEligibilityDetailsExist}
                            onSaveAndContinue={onHandleSaveAndContinue}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

export default withModalWrapper(TobaccoExemptContainer,modalConfig);
